import { useEffect } from "react";
import { useGenerationPolling } from "@/lib/hooks/useGenerationPolling";
import { useBooth } from "../BoothContext";

/**
 * The wait.
 *
 * Generation is asynchronous server-side, so this screen polls until the job
 * reaches a terminal state. There's no real progress signal from the provider,
 * so the ring is a time-based estimate — its job is to convince a guest standing
 * at a kiosk that something is happening, not to be accurate.
 */
export function ProcessingScreen() {
  const booth = useBooth();
  const polling = useGenerationPolling(booth.slug);

  useEffect(() => { booth.playCue("processing"); }, [booth]);

  useEffect(() => {
    if (booth.generationId) polling.track(booth.generationId);
    // Tracking is keyed on the generation, not on the polling identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booth.generationId]);

  useEffect(() => {
    if (polling.status === "completed" && polling.result?.image_url) {
      booth.playCue("result");
      booth.showResult({
        generationId: polling.result.id,
        shareToken: polling.result.public_token,
        url: polling.result.image_url,
        // The status payload carries prices and entitlements. Null is fine —
        // the result screen fetches them rather than assuming free.
        actions: polling.result.actions ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling.status, polling.result?.image_url]);

  const failed = polling.status === "failed";
  const message = booth.copy("processing_message", "Creating your photo");

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 px-8 text-center">
      {failed ? (
        <>
          <div className="text-6xl">😕</div>
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-bold" style={{ color: "var(--booth-headline)" }}>
              That didn't work
            </h2>
            <p className="max-w-sm text-sm opacity-75" style={{ color: "var(--booth-subtitle)" }}>
              {polling.error ?? "Something went wrong while creating your photo."}
            </p>
          </div>
          <button
            onClick={() => booth.go("capture")}
            className="rounded-xl px-10 py-4 text-lg font-bold uppercase tracking-wide"
            style={{ background: "var(--booth-secondary)", color: "var(--booth-cta-text)" }}
          >
            Try again
          </button>
        </>
      ) : (
        <>
          <ProgressRing progress={polling.progress} />
          <div className="flex flex-col gap-2">
            <h2
              className="text-xl uppercase tracking-[0.2em]"
              style={{ color: "var(--booth-headline)" }}
            >
              {message}
            </h2>
            <p className="text-sm opacity-60" style={{ color: "var(--booth-subtitle)" }}>
              This usually takes under a minute. Hang tight.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function ProgressRing({ progress }: { progress: number }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative">
      <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
        <circle cx="70" cy="70" r={radius} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="6"
          style={{ color: "var(--booth-secondary)" }} />
        <circle
          cx="70" cy="70" r={radius} fill="none" strokeWidth="6" strokeLinecap="round"
          stroke="currentColor"
          style={{
            color: "var(--booth-secondary)",
            strokeDasharray: circumference,
            strokeDashoffset: circumference * (1 - progress),
            transition: "stroke-dashoffset 600ms ease-out",
          }}
        />
      </svg>
      <span
        className="absolute inset-0 grid place-items-center text-lg font-bold tabular-nums"
        style={{ color: "var(--booth-headline)" }}
      >
        {Math.round(progress * 100)}%
      </span>
    </div>
  );
}
